import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Receipt,
  ShoppingCart,
  Package,
  Percent,
  X,
  Check,
  Loader2,
  Clock,
  XCircle,
  AlertTriangle,
  Grid3X3,
  List,
  Settings,
  LayoutDashboard,
  LogOut,
  User,
  ChevronDown,
  Sparkles,
  Star,
  Mail,
  Phone,
  Gift,
  FileText,
  Printer,
  DollarSign,
  Split,
  Maximize,
  Minimize,
  Bell,
  Fuel,
  Shield,
  ScanBarcode,
  Zap
} from 'lucide-react';
import AgeVerificationDialog from './AgeVerificationDialog';
import CartonPackDialog from './CartonPackDialog';
import { POSHeader } from './POSHeader';
import { POSProductGrid } from './POSProductGrid';
import { POSCartPanel } from './POSCartPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { LogoBrain } from '../../components/Logo';
import { ThemeToggle } from '../../components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSTerminal = ({ posUser, canVoidTransaction, canAccessReports, canChangeSettings }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Time and Fullscreen State
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // State
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const [currentShift, setCurrentShift] = useState(null);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [gridSize, setGridSize] = useState('small'); // compact default
  
  // Grid size configurations
  const GRID_SIZES = {
    small: {
      gridCols: 'grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8',
      gap: 'gap-2',
      padding: 'p-2',
      imageSize: 'h-6 w-6',
      imageContainer: 'h-10',
      nameSize: 'text-xs',
      skuSize: 'text-[10px]',
      priceSize: 'text-sm font-bold',
      stockSize: 'text-[10px]',
      label: 'Small'
    },
    medium: {
      gridCols: 'grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7',
      gap: 'gap-2',
      padding: 'p-2.5',
      imageSize: 'h-8 w-8',
      imageContainer: 'h-14',
      nameSize: 'text-xs',
      skuSize: 'text-[10px]',
      priceSize: 'text-base font-bold',
      stockSize: 'text-[10px]',
      label: 'Medium'
    },
    large: {
      gridCols: 'grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6',
      gap: 'gap-3',
      padding: 'p-3',
      imageSize: 'h-10 w-10',
      imageContainer: 'h-16',
      nameSize: 'text-sm',
      skuSize: 'text-xs',
      priceSize: 'text-lg font-bold',
      stockSize: 'text-[10px]',
      label: 'Large'
    }
  };
  
  // USA Tax State
  const [taxSettings, setTaxSettings] = useState({
    stateCode: '',
    stateName: '',
    taxRate: 0,
    hasLocalTax: false
  });
  const [usaStates, setUsaStates] = useState([]);
  
  // Customer
  const [customerName, setCustomerName] = useState('Walk-in Customer');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Discount
  const [discount, setDiscount] = useState({ type: 'percent', value: 0 });
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  
  // Payment
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Split Payment
  const [splitPayment, setSplitPayment] = useState({ enabled: false, cashAmount: '', cardAmount: '' });
  
  // Receipt & Digital Receipt
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [showDigitalReceiptDialog, setShowDigitalReceiptDialog] = useState(false);
  const [digitalReceiptData, setDigitalReceiptData] = useState({ email: '', phone: '', method: 'email' });
  const [sendingReceipt, setSendingReceipt] = useState(false);
  
  // Customer & Loyalty
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '', loyalty_points: 0 });
  const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState(0);
  
  // Coupons & Promo Codes
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  
  // Shift Dialog
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [showShiftReportDialog, setShowShiftReportDialog] = useState(false);
  const [shiftReport, setShiftReport] = useState(null);
  
  // Barcode Scanner
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  
  // Void confirmation
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [pendingVoidId, setPendingVoidId] = useState(null);
  
  // Hold & Recall Orders
  const [heldOrders, setHeldOrders] = useState([]);
  const [showHeldOrdersDialog, setShowHeldOrdersDialog] = useState(false);
  
  // Returns/Refunds
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundSearch, setRefundSearch] = useState('');
  const [refundTransaction, setRefundTransaction] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundItems, setRefundItems] = useState([]);
  
  // Quick Add Product
  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState({
    name: '',
    sku: '',
    barcode: '',
    category: '',
    cost_price: '',
    sell_price: '',
    stock_quantity: '10',
    low_stock_threshold: '5',
    image_url: '',
    use_icon: false,
    icon: '📦',
    icon_size: 'medium',
    icon_color: '#3b82f6'
  });
  const [addingProduct, setAddingProduct] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // Bulk Inventory Import
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkProducts, setBulkProducts] = useState([]);
  const [importingBulk, setImportingBulk] = useState(false);
  
  // Fuel Prepay (Gas Station)
  const [showFuelPrepayDialog, setShowFuelPrepayDialog] = useState(false);
  const [fuelPrepay, setFuelPrepay] = useState({
    pumpNumber: '',
    amount: '',
    grade: 'regular',
    customerName: ''
  });
  const [fuelGrades] = useState([
    { id: 'regular', name: 'Regular', price: 3.29, color: 'green' },
    { id: 'plus', name: 'Plus', price: 3.59, color: 'yellow' },
    { id: 'premium', name: 'Premium', price: 3.89, color: 'red' },
    { id: 'diesel', name: 'Diesel', price: 3.49, color: 'black' }
  ]);
  const [activePrepays, setActivePrepays] = useState([]);
  const [showFuelChangeDialog, setShowFuelChangeDialog] = useState(false);
  const [selectedPrepay, setSelectedPrepay] = useState(null);
  const [actualFuelAmount, setActualFuelAmount] = useState('');
  
  // Product Icon Options
  const PRODUCT_ICONS = {
    smoke_shop: ['🚬', '💨', '🔥', '🌿', '📜', '🎁', '💊', '🧪'],
    mobile_electronics: ['📱', '💻', '🔌', '🎧', '📺', '🔋', '💾', '🖥️'],
    retail_store: ['👕', '👗', '👟', '🎒', '⌚', '💍', '🕶️', '👜'],
    restaurant_cafe: ['☕', '🍔', '🍕', '🥤', '🍰', '🥗', '🍟', '🌮'],
    grocery_convenience: ['🥤', '🍪', '🍫', '🥛', '🍞', '🧃', '🍎', '🥚'],
    gas_station: ['⛽', '🥤', '🍪', '🚬', '🌭', '🍺', '🎰', '🔋'],
    pharmacy: ['💊', '💉', '🩹', '🧴', '🩺', '💊', '🌡️', '🧪'],
    clothing_fashion: ['👕', '👗', '👖', '👟', '👜', '🧥', '👒', '🧣'],
    salon_spa: ['✂️', '💅', '💆', '🧖', '💇', '🪮', '💄', '🧴'],
    auto_hardware: ['🔧', '🔩', '🛠️', '⚙️', '🔋', '🛞', '🚗', '🔌'],
    wholesale_distribution: ['📦', '🏷️', '📋', '🚚', '💰', '📊', '🏪', '💼'],
  };
  
  const ICON_SIZES = {
    small: 'h-8 w-8 text-2xl',
    medium: 'h-12 w-12 text-4xl',
    large: 'h-16 w-16 text-5xl'
  };
  
  // Business Type & Industry Features
  const [businessType, setBusinessType] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [businessTypes, setBusinessTypes] = useState([]);
  
  // Check if user is admin (can switch industries)
  const isAdmin = user?.role === 'admin';
  
  // Industry-specific features mapping
  const INDUSTRY_FEATURES = {
    retail_store: { 
      name: 'Retail Store', 
      icon: 'Store',
      features: ['Barcode Scanning', 'Inventory Alerts', 'Customer Loyalty'],
      color: 'blue'
    },
    smoke_shop: { 
      name: 'Smoke Shop', 
      icon: 'Cigarette',
      features: ['21+ Age Verify', 'Tobacco Tax', 'Compliance Logs'],
      color: 'amber',
      requiresAgeVerify: true
    },
    gas_station: { 
      name: 'Gas Station', 
      icon: 'Fuel',
      features: ['Fuel Pumps', 'C-Store', 'Tank Monitor'],
      color: 'orange',
      requiresAgeVerify: true,
      specialRoute: '/pos/gas-station'
    },
    mobile_electronics: { 
      name: 'Mobile & Electronics', 
      icon: 'Smartphone',
      features: ['IMEI Tracking', 'Repair Orders', 'Warranty Check'],
      color: 'purple'
    },
    clothing_fashion: { 
      name: 'Clothing & Fashion', 
      icon: 'Shirt',
      features: ['Size Variants', 'Style Filter', 'Season Tags'],
      color: 'pink'
    },
    restaurant_cafe: { 
      name: 'Restaurant & Cafe', 
      icon: 'UtensilsCrossed',
      features: ['Table Service', 'Kitchen Display', 'Split Bills'],
      color: 'orange'
    },
    pharmacy: { 
      name: 'Pharmacy', 
      icon: 'Pill',
      features: ['Rx Lookup', 'Expiry Alerts', 'Insurance'],
      color: 'green'
    },
    grocery_convenience: { 
      name: 'Grocery & Convenience', 
      icon: 'ShoppingBasket',
      features: ['Quick Keys', 'Weight Scale', 'Perishable Track'],
      color: 'emerald'
    },
    salon_spa: { 
      name: 'Salon & Spa', 
      icon: 'Scissors',
      features: ['Appointments', 'Staff Tips', 'Service Packages'],
      color: 'rose'
    },
    auto_hardware: { 
      name: 'Auto & Hardware', 
      icon: 'Wrench',
      features: ['Part Lookup', 'Vehicle Match', 'Warranty Claims'],
      color: 'slate'
    },
    wholesale_distribution: { 
      name: 'Wholesale & Distribution', 
      icon: 'Truck',
      features: ['B2B Pricing', 'Bulk Discounts', 'Route Planning'],
      color: 'indigo'
    }
  };
  
  // Age Verification (for smoke shops)
  const [showAgeVerifyDialog, setShowAgeVerifyDialog] = useState(false);
  const [ageVerifyData, setAgeVerifyData] = useState({ dob: '', idType: 'drivers_license', idNumber: '' });
  const [ageVerified, setAgeVerified] = useState(false);
  const [verificationLog, setVerificationLog] = useState(null);
  const [pendingAgeProduct, setPendingAgeProduct] = useState(null); // Product waiting for age check
  
  // Carton/Pack splitting
  const [showCartonDialog, setShowCartonDialog] = useState(false);
  const [pendingCartonProduct, setPendingCartonProduct] = useState(null);
  
  // Quick Keys
  const [quickKeys, setQuickKeys] = useState([]);
  
  // Industry-specific Quick Keys mapping
  const INDUSTRY_QUICK_KEYS = {
    smoke_shop: [
      { id: 'cigarettes', name: 'Cigarettes', icon: '🚬', color: 'amber' },
      { id: 'vapes', name: 'Vapes', icon: '💨', color: 'purple' },
      { id: 'rolling_papers', name: 'Rolling Papers', icon: '📜', color: 'green' },
      { id: 'lighters', name: 'Lighters', icon: '🔥', color: 'orange' },
      { id: 'cbd', name: 'CBD Products', icon: '🌿', color: 'emerald' },
      { id: 'accessories', name: 'Accessories', icon: '🎁', color: 'blue' },
    ],
    mobile_electronics: [
      { id: 'phones', name: 'Phones', icon: '📱', color: 'blue' },
      { id: 'cases', name: 'Cases', icon: '🛡️', color: 'purple' },
      { id: 'chargers', name: 'Chargers', icon: '🔌', color: 'green' },
      { id: 'screen_protectors', name: 'Screen Protectors', icon: '📺', color: 'slate' },
      { id: 'repairs', name: 'Repairs', icon: '🔧', color: 'orange' },
      { id: 'earbuds', name: 'Earbuds', icon: '🎧', color: 'pink' },
    ],
    retail_store: [
      { id: 'popular', name: 'Popular', icon: '⭐', color: 'amber' },
      { id: 'new_arrivals', name: 'New Arrivals', icon: '🆕', color: 'blue' },
      { id: 'sale', name: 'On Sale', icon: '🏷️', color: 'red' },
      { id: 'essentials', name: 'Essentials', icon: '🛒', color: 'green' },
      { id: 'gifts', name: 'Gifts', icon: '🎁', color: 'purple' },
      { id: 'seasonal', name: 'Seasonal', icon: '🌟', color: 'orange' },
    ],
    restaurant_cafe: [
      { id: 'drinks', name: 'Drinks', icon: '🥤', color: 'blue' },
      { id: 'food', name: 'Food', icon: '🍔', color: 'orange' },
      { id: 'desserts', name: 'Desserts', icon: '🍰', color: 'pink' },
      { id: 'combos', name: 'Combos', icon: '🍱', color: 'green' },
      { id: 'specials', name: 'Specials', icon: '⭐', color: 'amber' },
      { id: 'sides', name: 'Sides', icon: '🍟', color: 'yellow' },
    ],
    grocery_convenience: [
      { id: 'snacks', name: 'Snacks', icon: '🍪', color: 'amber' },
      { id: 'beverages', name: 'Beverages', icon: '🥤', color: 'blue' },
      { id: 'tobacco', name: 'Tobacco', icon: '🚬', color: 'slate' },
      { id: 'lottery', name: 'Lottery', icon: '🎰', color: 'green' },
      { id: 'dairy', name: 'Dairy', icon: '🥛', color: 'white' },
      { id: 'bread', name: 'Bread', icon: '🍞', color: 'orange' },
    ],
    gas_station: [
      { id: 'fuel', name: 'Fuel Sales', icon: '⛽', color: 'orange' },
      { id: 'drinks', name: 'Drinks', icon: '🥤', color: 'blue' },
      { id: 'snacks', name: 'Snacks', icon: '🍪', color: 'amber' },
      { id: 'tobacco', name: 'Tobacco', icon: '🚬', color: 'slate' },
      { id: 'lottery', name: 'Lottery', icon: '🎰', color: 'green' },
      { id: 'food', name: 'Hot Food', icon: '🌭', color: 'red' },
    ],
    pharmacy: [
      { id: 'otc', name: 'OTC Meds', icon: '💊', color: 'blue' },
      { id: 'vitamins', name: 'Vitamins', icon: '🌟', color: 'orange' },
      { id: 'first_aid', name: 'First Aid', icon: '🩹', color: 'red' },
      { id: 'personal_care', name: 'Personal Care', icon: '🧴', color: 'purple' },
      { id: 'baby', name: 'Baby Care', icon: '👶', color: 'pink' },
      { id: 'rx_pickup', name: 'Rx Pickup', icon: '📋', color: 'green' },
    ],
    clothing_fashion: [
      { id: 'mens', name: "Men's", icon: '👔', color: 'blue' },
      { id: 'womens', name: "Women's", icon: '👗', color: 'pink' },
      { id: 'kids', name: 'Kids', icon: '👶', color: 'purple' },
      { id: 'shoes', name: 'Shoes', icon: '👟', color: 'slate' },
      { id: 'accessories', name: 'Accessories', icon: '👜', color: 'amber' },
      { id: 'sale', name: 'Sale', icon: '🏷️', color: 'red' },
    ],
    salon_spa: [
      { id: 'haircut', name: 'Haircut', icon: '✂️', color: 'blue' },
      { id: 'color', name: 'Color', icon: '🎨', color: 'purple' },
      { id: 'nails', name: 'Nails', icon: '💅', color: 'pink' },
      { id: 'facial', name: 'Facial', icon: '🧖', color: 'green' },
      { id: 'massage', name: 'Massage', icon: '💆', color: 'amber' },
      { id: 'products', name: 'Products', icon: '🧴', color: 'slate' },
    ],
    auto_hardware: [
      { id: 'oil', name: 'Motor Oil', icon: '🛢️', color: 'amber' },
      { id: 'batteries', name: 'Batteries', icon: '🔋', color: 'green' },
      { id: 'tools', name: 'Tools', icon: '🔧', color: 'blue' },
      { id: 'parts', name: 'Parts', icon: '⚙️', color: 'slate' },
      { id: 'tires', name: 'Tires', icon: '🛞', color: 'black' },
      { id: 'cleaning', name: 'Cleaning', icon: '🧽', color: 'purple' },
    ],
    wholesale_distribution: [
      { id: 'bulk', name: 'Bulk Items', icon: '📦', color: 'amber' },
      { id: 'case_deals', name: 'Case Deals', icon: '🏷️', color: 'green' },
      { id: 'new_stock', name: 'New Stock', icon: '🆕', color: 'blue' },
      { id: 'clearance', name: 'Clearance', icon: '💰', color: 'red' },
      { id: 'seasonal', name: 'Seasonal', icon: '🌟', color: 'purple' },
      { id: 'popular', name: 'Popular', icon: '⭐', color: 'orange' },
    ],
  };
  
  // Industry-specific categories for quick add
  const INDUSTRY_CATEGORIES = {
    smoke_shop: ['Cigarettes', 'Vapes', 'E-Liquids', 'Rolling Papers', 'Lighters', 'CBD Products', 'Accessories', 'Tobacco'],
    mobile_electronics: ['Phones', 'Cases', 'Chargers', 'Screen Protectors', 'Cables', 'Earbuds', 'Repairs', 'Parts'],
    retail_store: ['General', 'Popular', 'New Arrivals', 'Sale', 'Essentials', 'Gifts'],
    restaurant_cafe: ['Drinks', 'Food', 'Desserts', 'Combos', 'Specials', 'Sides'],
    grocery_convenience: ['Snacks', 'Beverages', 'Tobacco', 'Lottery', 'Dairy', 'Bread'],
    gas_station: ['Fuel', 'Drinks', 'Snacks', 'Tobacco', 'Food', 'Alcohol', 'Lottery', 'Auto Supplies'],
    pharmacy: ['OTC Meds', 'Vitamins', 'First Aid', 'Personal Care', 'Baby Care', 'Rx'],
    clothing_fashion: ["Men's", "Women's", 'Kids', 'Shoes', 'Accessories', 'Sale'],
    salon_spa: ['Haircut', 'Color', 'Nails', 'Facial', 'Massage', 'Products'],
    auto_hardware: ['Motor Oil', 'Batteries', 'Tools', 'Parts', 'Tires', 'Cleaning'],
    wholesale_distribution: ['Bulk Items', 'Case Deals', 'New Stock', 'Clearance', 'Seasonal', 'Popular'],
  };
  
  // Barcode Scanner
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannerMode, setScannerMode] = useState(false);

  // Check for payment return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  // Load initial data
  useEffect(() => {
    loadTerminals();
    loadProducts();
    loadStats();
    loadUSAStates();
    loadBusinessTypes();
    loadUserBusinessType();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Fullscreen toggle function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Load current shift when terminal changes
  useEffect(() => {
    if (selectedTerminal) {
      loadCurrentShift();
    }
  }, [selectedTerminal]);

  // Load all business types (for admin dropdown)
  const loadBusinessTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/business-types/`);
      setBusinessTypes(res.data || []);
    } catch (err) {
      console.error('Failed to load business types:', err);
    }
  };

  // Load user's organization business type
  const loadUserBusinessType = async () => {
    try {
      // Check for URL parameter first (from Dashboard category selection)
      const urlIndustry = searchParams.get('industry');
      if (urlIndustry && INDUSTRY_FEATURES[urlIndustry]) {
        setBusinessType(INDUSTRY_FEATURES[urlIndustry]);
        setSelectedIndustry(urlIndustry);
        return;
      }
      
      const res = await axios.get(`${API_URL}/api/subscriptions/my-access`);
      if (res.data?.business_type) {
        const bt = res.data.business_type;
        setBusinessType(INDUSTRY_FEATURES[bt] || { name: bt, features: [] });
        setSelectedIndustry(bt);
      }
    } catch (err) {
      console.error('Failed to load business type:', err);
      // Default to retail_store if not found
      setBusinessType(INDUSTRY_FEATURES['retail_store']);
      setSelectedIndustry('retail_store');
    }
  };

  // Handle admin industry switch
  const handleIndustrySwitch = (industryId) => {
    const industryConfig = INDUSTRY_FEATURES[industryId];
    
    // If the industry has a special route, navigate to it
    if (industryConfig?.specialRoute) {
      toast.success(`Opening ${industryConfig.name} POS...`);
      navigate(industryConfig.specialRoute);
      return;
    }
    
    setSelectedIndustry(industryId);
    setBusinessType(industryConfig || { name: industryId, features: [] });
    setQuickKeys(INDUSTRY_QUICK_KEYS[industryId] || INDUSTRY_QUICK_KEYS['retail_store']);
    // Reset age verification when switching industries
    setAgeVerified(false);
    setVerificationLog(null);
    toast.success(`Switched to ${industryConfig?.name || industryId} mode`);
  };

  // Update quick keys when industry changes
  useEffect(() => {
    if (selectedIndustry) {
      setQuickKeys(INDUSTRY_QUICK_KEYS[selectedIndustry] || INDUSTRY_QUICK_KEYS['retail_store']);
    }
  }, [selectedIndustry]);

  // Check if age verification is required before checkout
  const requiresAgeVerification = () => {
    const hasRestrictedItems = cart.some(item => item.age_restricted);
    return hasRestrictedItems && !ageVerified;
  };

  // Handle quick key click - filter products by category
  const handleQuickKeyClick = (quickKey) => {
    setSearchQuery(quickKey.name);
    loadProducts(quickKey.name);
    toast.info(`Showing ${quickKey.name}`);
  };

  // Quick Add Product function
  const handleQuickAddProduct = async () => {
    if (!quickAddProduct.name || !quickAddProduct.sell_price) {
      toast.error('Please enter product name and sell price');
      return;
    }
    
    setAddingProduct(true);
    try {
      // Build image_url: either AI-generated URL, icon: prefix, or empty
      let imageUrl = '';
      if (quickAddProduct.use_icon && quickAddProduct.icon) {
        imageUrl = `icon:${quickAddProduct.icon}`;
      } else if (quickAddProduct.image_url) {
        imageUrl = quickAddProduct.image_url;
      }

      const productData = {
        name: quickAddProduct.name,
        sku: quickAddProduct.sku || `SKU-${Date.now()}`,
        barcode: quickAddProduct.barcode || '',
        category: quickAddProduct.category || (INDUSTRY_CATEGORIES[selectedIndustry]?.[0] || 'General'),
        cost_price: parseFloat(quickAddProduct.cost_price) || parseFloat(quickAddProduct.sell_price) * 0.6,
        sell_price: parseFloat(quickAddProduct.sell_price),
        stock_quantity: parseInt(quickAddProduct.stock_quantity) || 10,
        low_stock_threshold: parseInt(quickAddProduct.low_stock_threshold) || 5,
        unit: 'piece',
        is_active: true,
        image_url: imageUrl || null
      };
      
      const res = await axios.post(`${API_URL}/api/procurement/products`, productData);
      toast.success(`Product "${quickAddProduct.name}" added successfully!`);
      
      // Reset form
      setQuickAddProduct({
        name: '',
        sku: '',
        barcode: '',
        category: '',
        cost_price: '',
        sell_price: '',
        stock_quantity: '10',
        low_stock_threshold: '5',
        image_url: '',
        use_icon: false,
        icon: '📦',
      });
      setShowQuickAddDialog(false);
      
      // Reload products
      loadProducts();
      
      // Optionally add to cart immediately
      if (res.data?.id) {
        addToCart(res.data);
      }
    } catch (err) {
      console.error('Failed to add product:', err);
      toast.error('Failed to add product. Please try again.');
    } finally {
      setAddingProduct(false);
    }
  };

  // Check stock level and return status
  const getStockStatus = (product) => {
    const qty = product.stock_quantity || 0;
    const threshold = product.low_stock_threshold || 5;
    
    if (qty <= 0) {
      return { status: 'out', label: 'Out of Stock', color: 'red' };
    } else if (qty <= threshold) {
      return { status: 'low', label: `Only ${qty} left!`, color: 'amber' };
    } else {
      return { status: 'ok', label: `${qty} in stock`, color: 'green' };
    }
  };

  // Load Sample Products for the current industry
  const loadSampleProducts = async () => {
    if (!selectedIndustry) {
      toast.error('Please select an industry first');
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/business-types/${selectedIndustry}/seed-products`);
      toast.success(`${res.data.message}. ${res.data.products_skipped} already existed.`);
      // Reload products
      loadProducts();
    } catch (err) {
      console.error('Failed to seed products:', err);
      toast.error('Failed to load sample products');
    }
  };

  // AI Image Generation for Product
  const generateProductImage = async () => {
    if (!quickAddProduct.name) {
      toast.error('Please enter product name first');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const industryContext = businessType?.name || 'retail';
      const prompt = `Product photo of ${quickAddProduct.name} for ${industryContext} store, professional product photography, white background, high quality, commercial style`;
      
      const res = await axios.post(`${API_URL}/api/ai/generate-image`, {
        prompt: prompt,
        size: '256x256'
      });
      
      if (res.data?.image_url) {
        setQuickAddProduct(prev => ({ ...prev, image_url: res.data.image_url, use_icon: false }));
        toast.success('Product image generated!');
      }
    } catch (err) {
      console.error('Failed to generate image:', err);
      toast.error('Image generation failed. Using icon instead.');
      setQuickAddProduct(prev => ({ ...prev, use_icon: true }));
    } finally {
      setGeneratingImage(false);
    }
  };

  // Bulk Import Products
  const loadBulkProductTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/business-types/${selectedIndustry}/sample-products`);
      const products = res.data.products.map((p, idx) => ({
        ...p,
        id: `temp-${idx}`,
        selected: true,
        sell_price: p.sell_price.toString(),
        cost_price: p.cost_price.toString(),
        stock_quantity: p.stock_quantity.toString()
      }));
      setBulkProducts(products);
      setShowBulkImportDialog(true);
    } catch (err) {
      toast.error('Failed to load product template');
    }
  };

  const handleBulkImport = async () => {
    const selectedProducts = bulkProducts.filter(p => p.selected);
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    setImportingBulk(true);
    let added = 0;
    let failed = 0;
    
    for (const product of selectedProducts) {
      try {
        await axios.post(`${API_URL}/api/procurement/products`, {
          name: product.name,
          sku: product.sku || `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
          barcode: product.barcode || '',
          category: product.category,
          cost_price: parseFloat(product.cost_price) || 0,
          sell_price: parseFloat(product.sell_price) || 0,
          stock_quantity: parseInt(product.stock_quantity) || 10,
          low_stock_threshold: 5,
          unit: 'piece',
          is_active: true,
          image_url: product.image_url || null
        });
        added++;
      } catch (err) {
        failed++;
      }
    }
    
    setImportingBulk(false);
    setShowBulkImportDialog(false);
    toast.success(`Imported ${added} products${failed > 0 ? `, ${failed} failed` : ''}`);
    loadProducts();
  };

  // ==================== FUEL PREPAY FUNCTIONS ====================
  
  const handleFuelPrepay = () => {
    if (!fuelPrepay.pumpNumber || !fuelPrepay.amount) {
      toast.error('Please enter pump number and amount');
      return;
    }
    
    const amount = parseFloat(fuelPrepay.amount);
    if (amount <= 0 || amount > 500) {
      toast.error('Amount must be between $0.01 and $500');
      return;
    }
    
    const pumpNum = parseInt(fuelPrepay.pumpNumber);
    if (pumpNum < 1 || pumpNum > 24) {
      toast.error('Pump number must be between 1 and 24');
      return;
    }
    
    // Check if pump already has active prepay
    if (activePrepays.find(p => p.pumpNumber === pumpNum && p.status === 'active')) {
      toast.error(`Pump ${pumpNum} already has an active prepay`);
      return;
    }
    
    const selectedGrade = fuelGrades.find(g => g.id === fuelPrepay.grade);
    const prepayItem = {
      id: `PREPAY-${Date.now()}`,
      pumpNumber: pumpNum,
      amount: amount,
      grade: selectedGrade,
      customerName: fuelPrepay.customerName || 'Walk-in',
      status: 'active',
      createdAt: new Date().toISOString()
    };
    
    // Add to active prepays
    setActivePrepays(prev => [...prev, prepayItem]);
    
    // Add to cart as a fuel prepay item
    const cartItem = {
      id: `fuel-prepay-${pumpNum}-${Date.now()}`,
      name: `Fuel Prepay - Pump #${pumpNum}`,
      sell_price: amount,
      quantity: 1,
      category: 'Fuel',
      is_fuel_prepay: true,
      prepay_id: prepayItem.id,
      pump_number: pumpNum,
      grade: selectedGrade.name
    };
    
    setCart(prev => [...prev, cartItem]);
    
    // Reset form
    setFuelPrepay({ pumpNumber: '', amount: '', grade: 'regular', customerName: '' });
    setShowFuelPrepayDialog(false);
    
    toast.success(`Prepay $${amount.toFixed(2)} added for Pump #${pumpNum}`);
  };
  
  const handleFuelChange = () => {
    if (!selectedPrepay || !actualFuelAmount) {
      toast.error('Please enter the actual fuel amount');
      return;
    }
    
    const actual = parseFloat(actualFuelAmount);
    const prepayAmount = selectedPrepay.amount;
    const change = prepayAmount - actual;
    
    if (actual > prepayAmount) {
      toast.error('Actual amount cannot exceed prepay amount');
      return;
    }
    
    // Update the prepay status
    setActivePrepays(prev => prev.map(p => 
      p.id === selectedPrepay.id 
        ? { ...p, status: 'completed', actualAmount: actual, change: change }
        : p
    ));
    
    if (change > 0) {
      toast.info(`Change due to customer: $${change.toFixed(2)}`);
    } else {
      toast.success('Fuel transaction completed - exact amount');
    }
    
    setShowFuelChangeDialog(false);
    setSelectedPrepay(null);
    setActualFuelAmount('');
  };
  
  const cancelPrepay = (prepayId) => {
    // Remove from active prepays
    setActivePrepays(prev => prev.filter(p => p.id !== prepayId));
    
    // Remove from cart if present
    setCart(prev => prev.filter(item => item.prepay_id !== prepayId));
    
    toast.info('Prepay cancelled');
  };

  const loadUSAStates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/tax/states`);
      setUsaStates(res.data);
      // Load tax state from company settings
      const settingsRes = await axios.get(`${API_URL}/api/settings/company/public`);
      const savedState = settingsRes.data?.tax_state_code;
      const savedRate = settingsRes.data?.tax_rate;
      if (savedState) {
        const stateInfo = res.data.find(s => s.code === savedState);
        setTaxSettings({
          stateCode: savedState,
          stateName: stateInfo?.name || savedState,
          taxRate: stateInfo?.rate ?? savedRate ?? 0,
          hasLocalTax: true
        });
      } else if (savedRate) {
        setTaxSettings({
          stateCode: '',
          stateName: settingsRes.data?.tax_name || 'Tax',
          taxRate: savedRate,
          hasLocalTax: false
        });
      }
    } catch (err) {
      console.error('Failed to load tax settings:', err);
    }
  };

  // Load customers
  const loadCustomers = async (search = '') => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/customers${search ? `?search=${search}` : ''}`);
      setCustomers(res.data || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  // Create new customer
  const handleCreateCustomer = async () => {
    if (!newCustomer.name) {
      toast.error('Customer name is required');
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/pos/customers`, {
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone,
        loyalty_points: 0
      });
      toast.success(`Customer "${newCustomer.name}" created`);
      setSelectedCustomer(res.data);
      setShowNewCustomerDialog(false);
      setNewCustomer({ name: '', email: '', phone: '', loyalty_points: 0 });
    } catch (err) {
      toast.error('Failed to create customer');
    }
  };

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoCode) {
      toast.error('Please enter a promo code');
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/pos/promotions/check?code=${promoCode}&subtotal=${totals.subtotal}`);
      if (res.data?.applicable) {
        setAppliedPromo(res.data);
        toast.success(`Promo applied: ${res.data.discount_amount ? '$' + res.data.discount_amount : res.data.discount_percent + '%'} off`);
        setShowPromoDialog(false);
      } else {
        toast.error(res.data?.message || 'Promo code not valid');
      }
    } catch (err) {
      toast.error('Invalid or expired promo code');
    }
  };

  // Send digital receipt
  const handleSendDigitalReceipt = async () => {
    if (!currentReceipt) {
      toast.error('No receipt to send');
      return;
    }
    
    if (digitalReceiptData.method === 'email' && !digitalReceiptData.email) {
      toast.error('Please enter email address');
      return;
    }
    if (digitalReceiptData.method === 'sms' && !digitalReceiptData.phone) {
      toast.error('Please enter phone number');
      return;
    }
    
    setSendingReceipt(true);
    try {
      await axios.post(`${API_URL}/api/pos/receipts/send`, {
        receipt_id: currentReceipt.id || currentReceipt.transaction_id,
        method: digitalReceiptData.method,
        email: digitalReceiptData.email,
        phone: digitalReceiptData.phone
      });
      toast.success(`Receipt sent via ${digitalReceiptData.method}`);
      setShowDigitalReceiptDialog(false);
    } catch (err) {
      toast.error('Failed to send receipt');
    } finally {
      setSendingReceipt(false);
    }
  };

  // Generate shift report
  const generateShiftReport = async () => {
    if (!currentShift?.id) {
      toast.error('No active shift');
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/pos/shifts/${currentShift.id}/report`);
      setShiftReport(res.data);
      setShowShiftReportDialog(true);
    } catch (err) {
      toast.error('Failed to generate shift report');
    }
  };

  // Calculate loyalty points earned (1 point per $1 spent)
  const calculateLoyaltyPointsEarned = () => {
    return Math.floor(totals.total);
  };

  // Calculate loyalty discount (100 points = $1 off)
  const calculateLoyaltyDiscount = () => {
    return loyaltyPointsToRedeem / 100;
  };

  const handleStateChange = async (stateCode) => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/tax/state/${stateCode}`);
      setTaxSettings({
        stateCode: res.data.state_code,
        stateName: res.data.state_name,
        taxRate: res.data.state_tax_rate,
        hasLocalTax: res.data.has_local_tax
      });
      toast.success(`Tax rate updated: ${res.data.state_name} (${res.data.state_tax_rate}%)`);
    } catch (err) {
      toast.error('Failed to load state tax info');
    }
  };

  // Barcode Scanner Handler
  const handleBarcodeScanned = async (barcode) => {
    if (!barcode) return;
    
    try {
      const res = await axios.post(`${API_URL}/api/pos/products/barcode-lookup?barcode=${barcode}`);
      if (res.data) {
        addToCart(res.data);
        toast.success(`Scanned: ${res.data.name}`);
        setBarcodeInput('');
      }
    } catch (err) {
      // Product not found — offer to Quick Add with barcode pre-filled
      toast.error(`Not found: ${barcode}`, {
        description: 'Opening Quick Add...',
        duration: 2000,
      });
      setQuickAddProduct(prev => ({
        ...prev,
        barcode: barcode,
        sku: barcode,
        name: '',
        cost_price: '',
        sell_price: '',
      }));
      setShowQuickAddDialog(true);
      setBarcodeInput('');
    }
  };

  // Global barcode scanner listener — USB scanners send characters fast + Enter
  useEffect(() => {
    let buffer = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e) => {
      // Skip if user is typing in an input field (except the dedicated scan input)
      const target = e.target;
      const isScanInput = target.dataset?.testid === 'scan-mode-input';
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      if (isInputField && !isScanInput) return;

      const now = Date.now();
      
      if (e.key === 'Enter' && buffer.length >= 3) {
        e.preventDefault();
        handleBarcodeScanned(buffer.trim());
        buffer = '';
        return;
      }
      
      // USB scanners send chars within ~50ms of each other
      if (now - lastKeyTime > 100) {
        buffer = '';
      }
      
      if (e.key.length === 1) {
        buffer += e.key;
        lastKeyTime = now;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadTerminals = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/terminals`);
      let terminalList = res.data;
      
      // Auto-create default register if none exist
      if (terminalList.length === 0) {
        try {
          const createRes = await axios.post(`${API_URL}/api/pos/terminals`, {
            name: 'Register 1',
            location: 'Main Counter'
          });
          terminalList = [createRes.data];
        } catch (createErr) {
          console.error('Failed to auto-create terminal:', createErr);
        }
      }
      
      setTerminals(terminalList);
      if (terminalList.length > 0) {
        setSelectedTerminal(terminalList[0].id);
      }
    } catch (err) {
      console.error('Failed to load terminals:', err);
    }
  };

  const loadProducts = async (query = '') => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/products/search`, {
        params: { q: query, limit: 50 }
      });
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadCurrentShift = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/shifts/current/${selectedTerminal}`);
      setCurrentShift(res.data.shift || res.data);
    } catch (err) {
      console.error('Failed to load shift:', err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Search products
  const handleSearch = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    loadProducts(query);
  }, []);

  // Cart functions
  const addToCart = (product) => {
    // Check if product is age-restricted and customer isn't verified yet
    const isRestricted = product.age_restricted || 
      (product.category && ['cigarettes', 'cigars', 'vapes', 'hookah', 'pipes', 'rolling', 'cbd', 'tobacco'].some(
        c => product.category?.toLowerCase().includes(c)
      ));
    
    if (isRestricted && !ageVerified) {
      setPendingAgeProduct(product);
      setShowAgeVerifyDialog(true);
      return;
    }

    // Check if product has carton_size configured (carton/pack splitting)
    if (product.carton_size && product.carton_size > 1) {
      setPendingCartonProduct(product);
      setShowCartonDialog(true);
      return;
    }

    addItemToCartDirect(product);
  };

  const addItemToCartDirect = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        quantity: 1,
        unit_price: product.sell_price || product.cost_price * 1.3,
        discount_percent: 0,
        age_restricted: product.age_restricted || false
      }];
    });
  };

  // Age verification callbacks
  const handleAgeVerifySuccess = async (result) => {
    setAgeVerified(true);
    setVerificationLog(result);
    setShowAgeVerifyDialog(false);
    
    // Add the pending product (check carton splitting first)
    if (pendingAgeProduct) {
      if (pendingAgeProduct.carton_size && pendingAgeProduct.carton_size > 1) {
        setPendingCartonProduct(pendingAgeProduct);
        setShowCartonDialog(true);
      } else {
        addItemToCartDirect(pendingAgeProduct);
      }
      setPendingAgeProduct(null);
    }

    toast.success(`Age verified: ${result.age} years old (${result.method})`);
    
    // Log verification
    try {
      await axios.post(`${API_URL}/api/pos/age-verify`, {
        dob: result.dob,
        id_type: result.idType,
        id_number: result.idNumber || 'N/A',
        verified: true,
        age: result.age,
        method: result.method
      });
    } catch (err) {
      console.error('Failed to log verification:', err);
    }
  };

  const handleAgeVerifyDenied = async (result) => {
    setAgeVerified(false);
    setPendingAgeProduct(null);
    setShowAgeVerifyDialog(false);
    toast.error(`Customer is ${result.age} years old. Must be 21+ to purchase this product.`);
    
    try {
      await axios.post(`${API_URL}/api/pos/age-verify`, {
        dob: result.dob,
        verified: false,
        age: result.age,
        reason: result.reason
      });
    } catch (err) {
      console.error('Failed to log verification:', err);
    }
  };

  const handleAgeVerifyClose = () => {
    setShowAgeVerifyDialog(false);
    setPendingAgeProduct(null);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setDiscount({ type: 'percent', value: 0 });
    setCustomerName('Walk-in Customer');
    setCustomerPhone('');
  };

  // Hold current order
  const holdCurrentOrder = () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    const held = {
      id: `HOLD-${Date.now()}`,
      items: [...cart],
      customer: customerName,
      discount: { ...discount },
      heldAt: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      total: totals.total
    };
    setHeldOrders(prev => [...prev, held]);
    clearCart();
    toast.success(`Order held (${held.items.length} items)`);
  };

  // Recall held order
  const recallOrder = (orderId) => {
    const order = heldOrders.find(o => o.id === orderId);
    if (!order) return;
    if (cart.length > 0) {
      // Hold current cart first
      holdCurrentOrder();
    }
    setCart(order.items);
    setCustomerName(order.customer);
    setDiscount(order.discount);
    setHeldOrders(prev => prev.filter(o => o.id !== orderId));
    setShowHeldOrdersDialog(false);
    toast.success('Order recalled');
  };

  // Process refund
  const handleRefundLookup = async () => {
    if (!refundSearch.trim()) return;
    try {
      const res = await axios.get(`${API_URL}/api/pos/transactions/${refundSearch.trim()}`);
      if (res.data && res.data.status === 'completed') {
        setRefundTransaction(res.data);
        setRefundItems(res.data.items?.map(i => ({ ...i, refund: true })) || []);
      } else {
        toast.error('Transaction not found or not completed');
      }
    } catch (err) {
      toast.error('Transaction not found');
    }
  };

  const processRefund = async () => {
    if (!refundTransaction || !refundReason.trim()) {
      toast.error('Please select items and enter reason');
      return;
    }
    const itemsToRefund = refundItems.filter(i => i.refund);
    if (itemsToRefund.length === 0) { toast.error('Select at least one item'); return; }
    
    try {
      await axios.post(`${API_URL}/api/pos/transactions/${refundTransaction.id}/void`, null, {
        params: { reason: `REFUND: ${refundReason}` }
      });
      toast.success('Refund processed successfully');
      setShowRefundDialog(false);
      setRefundTransaction(null);
      setRefundReason('');
      setRefundSearch('');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    }
  };

  // Calculate totals with USA state tax
  const calculateTotals = () => {
    let subtotal = 0;
    cart.forEach(item => {
      subtotal += item.unit_price * item.quantity;
    });
    
    let discountAmount = 0;
    if (discount.type === 'percent') {
      discountAmount = subtotal * (discount.value / 100);
    } else {
      discountAmount = discount.value;
    }
    
    // Use state tax rate instead of fixed 15% VAT
    const taxRate = taxSettings.taxRate || 0;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = taxableAmount * (taxRate / 100);
    const total = subtotal - discountAmount + taxAmount;
    
    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      taxRate: taxRate,
      taxState: taxSettings.stateName || '',
      taxAmount: taxAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const totals = calculateTotals();

  // Open shift
  const handleOpenShift = async () => {
    // Auto-select if only one terminal
    const terminalId = selectedTerminal || (terminals.length === 1 ? terminals[0].id : null);
    
    if (!terminalId) {
      toast.error('Please select a register');
      return;
    }
    
    // Ensure selectedTerminal state is set
    if (!selectedTerminal && terminalId) {
      setSelectedTerminal(terminalId);
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/pos/shifts/open`, {
        terminal_id: terminalId,
        opening_cash: parseFloat(openingCash) || 0
      }, {
        params: { cashier_id: posUser?.id || 'user1', cashier_name: posUser?.name || user?.full_name || 'Cashier' }
      });
      setCurrentShift(res.data);
      setShowShiftDialog(false);
      toast.success('Shift opened successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open shift');
    }
  };

  // Process payment
  const handlePayment = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    if (!selectedTerminal && terminals.length === 1) {
      setSelectedTerminal(terminals[0].id);
    }
    
    const terminalId = selectedTerminal || (terminals.length === 1 ? terminals[0].id : null);
    if (!terminalId) {
      toast.error('No register selected');
      return;
    }

    if (!currentShift?.id) {
      toast.error('Please open a shift first');
      return;
    }

    setProcessing(true);
    
    try {
      // Create transaction
      const txnRes = await axios.post(`${API_URL}/api/pos/transactions`, {
        terminal_id: terminalId,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount_percent: item.discount_percent || 0
        })),
        customer_name: customerName,
        customer_phone: customerPhone,
        discount: discount.value > 0 ? discount : null,
        payment_method: paymentMethod
      });
      
      const transactionId = txnRes.data.id;
      
      if (paymentMethod === 'cash') {
        // Cash payment
        const cashPaid = parseFloat(cashAmount);
        const total = parseFloat(totals.total);
        
        if (cashPaid < total) {
          toast.error('Insufficient payment amount');
          setProcessing(false);
          return;
        }
        
        const result = await axios.post(`${API_URL}/api/pos/checkout/cash`, null, {
          params: { transaction_id: transactionId, amount_paid: cashPaid }
        });
        
        // Show receipt
        const receiptRes = await axios.get(`${API_URL}/api/pos/receipt/${transactionId}`);
        setCurrentReceipt({ ...receiptRes.data, change_due: result.data.change_due });
        setShowPaymentDialog(false);
        setShowReceiptDialog(true);
        clearCart();
        loadStats();
        toast.success('Payment successful!');
        
      } else {
        // Card/Mada payment via Stripe
        const origin = window.location.origin;
        const checkoutRes = await axios.post(`${API_URL}/api/pos/checkout/card`, null, {
          params: {
            transaction_id: transactionId,
            origin_url: origin + '/pos-terminal',
            payment_type: paymentMethod
          }
        });
        
        // Redirect to Stripe
        window.location.href = checkoutRes.data.checkout_url;
      }
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment verification timed out');
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/pos/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        
        if (res.data.transaction_id) {
          const receiptRes = await axios.get(`${API_URL}/api/pos/receipt/${res.data.transaction_id}`);
          setCurrentReceipt(receiptRes.data);
          setShowReceiptDialog(true);
        }
        
        clearCart();
        loadStats();
        window.history.replaceState({}, '', '/pos-terminal');
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      toast.error('Failed to verify payment');
    }
  };

  // Void transaction (manager/admin only)
  const handleVoidTransaction = async () => {
    if (!canVoidTransaction) {
      toast.error('You do not have permission to void transactions');
      return;
    }
    
    // For now, just clear the cart
    clearCart();
    setShowVoidDialog(false);
    toast.success('Transaction voided');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex flex-col bg-slate-100 dark:bg-slate-900 overflow-hidden">
      <POSHeader
        user={user} isAdmin={isAdmin} businessType={businessType}
        selectedIndustry={selectedIndustry} INDUSTRY_FEATURES={INDUSTRY_FEATURES}
        handleIndustrySwitch={handleIndustrySwitch} currentTime={currentTime}
        currentShift={currentShift} terminals={terminals} selectedTerminal={selectedTerminal}
        setShowShiftDialog={setShowShiftDialog} setShowAgeVerifyDialog={setShowAgeVerifyDialog}
        toggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen}
        navigate={navigate} handleLogout={handleLogout}
      />

      {/* Main POS Content */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden">

      <POSProductGrid
        searchQuery={searchQuery} handleSearch={handleSearch}
        scannerMode={scannerMode} setScannerMode={setScannerMode}
        barcodeInput={barcodeInput} setBarcodeInput={setBarcodeInput}
        handleBarcodeScanned={handleBarcodeScanned}
        setShowQuickAddDialog={setShowQuickAddDialog}
        selectedIndustry={selectedIndustry} setShowFuelPrepayDialog={setShowFuelPrepayDialog}
        quickKeys={quickKeys} handleQuickKeyClick={handleQuickKeyClick}
        products={products} viewMode={viewMode} gridSize={gridSize}
        GRID_SIZES={GRID_SIZES} addToCart={addToCart} getStockStatus={getStockStatus}
        businessType={businessType} loadSampleProducts={loadSampleProducts}
      />
      
      <POSCartPanel
        cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} totals={totals}
        customerName={customerName} setCustomerName={setCustomerName}
        selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
        loadCustomers={loadCustomers} setShowCustomerDialog={setShowCustomerDialog}
        appliedPromo={appliedPromo} setAppliedPromo={setAppliedPromo} setShowPromoDialog={setShowPromoDialog}
        loyaltyPointsToRedeem={loyaltyPointsToRedeem} setLoyaltyPointsToRedeem={setLoyaltyPointsToRedeem}
        calculateLoyaltyPointsEarned={calculateLoyaltyPointsEarned}
        discount={discount} setShowDiscountDialog={setShowDiscountDialog}
        setPaymentMethod={setPaymentMethod} setCashAmount={setCashAmount}
        setShowPaymentDialog={setShowPaymentDialog} setSplitPayment={setSplitPayment}
        currentShift={currentShift} setShowVoidDialog={setShowVoidDialog}
        requiresAgeVerification={requiresAgeVerification}
        ageVerified={ageVerified} verificationLog={verificationLog}
        setShowAgeVerifyDialog={setShowAgeVerifyDialog}
        heldOrders={heldOrders} holdCurrentOrder={holdCurrentOrder}
        setShowHeldOrdersDialog={setShowHeldOrdersDialog}
        setShowRefundDialog={setShowRefundDialog}
      />
      </div>
      {/* Dialogs */}
      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Discount Type</Label>
              <Select value={discount.type} onValueChange={(v) => setDiscount(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (SAR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                type="number"
                value={discount.value}
                onChange={(e) => setDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                className="text-xl h-12"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscount({ type: 'percent', value: 0 })}>
              Clear
            </Button>
            <Button onClick={() => setShowDiscountDialog(false)}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {paymentMethod === 'cash' ? (
                <><Banknote className="h-6 w-6 text-emerald-600" /> Cash Payment</>
              ) : paymentMethod === 'mada' ? (
                <><CreditCard className="h-6 w-6 text-purple-600" /> Mada Payment</>
              ) : (
                <><CreditCard className="h-6 w-6 text-blue-600" /> Card Payment</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center py-6 bg-slate-100 dark:bg-slate-800 rounded-xl">
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-5xl font-bold text-primary">${totals.total}</p>
            </div>
            
            {paymentMethod === 'cash' && (
              <>
                <div>
                  <Label>Amount Received</Label>
                  <Input
                    type="number"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    className="text-3xl h-16 text-center font-bold"
                    autoFocus
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  {[5, 10, 20, 50, 100].map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      className="h-11 text-base font-semibold"
                      onClick={() => setCashAmount(amount.toString())}
                    >
                      ${amount}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    className="h-11 text-sm font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => setCashAmount(totals.total)}
                  >
                    Exact
                  </Button>
                </div>
                
                {parseFloat(cashAmount) >= parseFloat(totals.total) && (
                  <div className="text-center p-4 bg-emerald-100 dark:bg-emerald-950 rounded-xl">
                    <p className="text-sm text-muted-foreground">Change Due</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      ${(parseFloat(cashAmount) - parseFloat(totals.total)).toFixed(2)}
                    </p>
                  </div>
                )}
              </>
            )}
            
            {(paymentMethod === 'card' || paymentMethod === 'mada') && (
              <div className="text-center py-6">
                <p className="text-muted-foreground">
                  You will be redirected to secure payment page
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="h-12">
              Cancel
            </Button>
            <Button 
              onClick={handlePayment}
              disabled={processing || (paymentMethod === 'cash' && parseFloat(cashAmount) < parseFloat(totals.total))}
              className={`h-12 px-8 ${
                paymentMethod === 'cash' ? 'bg-emerald-600 hover:bg-emerald-700' : 
                paymentMethod === 'mada' ? 'bg-purple-600 hover:bg-purple-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {processing ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Check className="h-5 w-5 mr-2" /> Complete Payment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> Receipt
            </DialogTitle>
          </DialogHeader>
          
          {currentReceipt && (
            <div className="font-mono text-sm space-y-3 bg-white dark:bg-slate-900 p-4 rounded-lg border">
              <div className="text-center border-b pb-3">
                <p className="font-bold text-lg">{currentReceipt.store_name}</p>
                <p className="text-xs text-muted-foreground">{currentReceipt.receipt_number}</p>
                <p className="text-xs text-muted-foreground">{currentReceipt.date} {currentReceipt.time}</p>
              </div>
              
              <div className="space-y-1">
                {currentReceipt.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span>{item.name} x{item.quantity}</span>
                    <span>${item.line_total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Subtotal</span>
                  <span>${currentReceipt.subtotal?.toFixed(2)}</span>
                </div>
                {currentReceipt.discount_amount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600">
                    <span>Discount</span>
                    <span>-${currentReceipt.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span>VAT (15%)</span>
                  <span>${currentReceipt.tax_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold pt-1 border-t">
                  <span>TOTAL</span>
                  <span>${currentReceipt.total?.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="border-t pt-2 text-xs">
                <div className="flex justify-between">
                  <span>Paid ({currentReceipt.payment_method})</span>
                  <span>${currentReceipt.amount_paid?.toFixed(2)}</span>
                </div>
                {currentReceipt.change_due > 0 && (
                  <div className="flex justify-between font-bold text-emerald-600">
                    <span>Change</span>
                    <span>${currentReceipt.change_due?.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <div className="text-center pt-3 border-t">
                <p className="text-xs">{currentReceipt.thank_you_message}</p>
              </div>
            </div>
          )}
          
          <div className="flex flex-col gap-2">
            <p className="text-xs text-center text-muted-foreground font-medium">Receipt Options</p>
            <div className="grid grid-cols-4 gap-2">
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1 flex-col py-1" onClick={() => window.print()}>
                <Receipt className="h-3.5 w-3.5" /> Print
              </Button>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1 flex-col py-1"
                onClick={() => { setShowReceiptDialog(false); setDigitalReceiptData(prev => ({...prev, method: 'sms'})); setShowDigitalReceiptDialog(true); }}>
                <Phone className="h-3.5 w-3.5" /> SMS
              </Button>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1 flex-col py-1"
                onClick={() => { setShowReceiptDialog(false); setDigitalReceiptData(prev => ({...prev, method: 'email'})); setShowDigitalReceiptDialog(true); }}>
                <Mail className="h-3.5 w-3.5" /> Email
              </Button>
              <Button size="sm" className="h-9 text-xs gap-1 flex-col py-1" onClick={() => setShowReceiptDialog(false)}>
                <X className="h-3.5 w-3.5" /> Skip
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Open Shift Dialog */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-emerald-500" />
              Open New Shift
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Register Picker — only shown if multiple registers */}
            {terminals.length > 1 && (
              <div>
                <Label>Select Register</Label>
                <Select value={selectedTerminal} onValueChange={setSelectedTerminal}>
                  <SelectTrigger className="h-12" data-testid="shift-terminal-select">
                    <SelectValue placeholder="Choose register..." />
                  </SelectTrigger>
                  <SelectContent>
                    {terminals.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.location ? `— ${t.location}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show current register if auto-selected */}
            {terminals.length === 1 && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium">{terminals[0]?.name || 'Register 1'}</span>
                {terminals[0]?.location && <span className="text-xs text-muted-foreground">— {terminals[0].location}</span>}
              </div>
            )}

            <div>
              <Label>Opening Cash Amount ($)</Label>
              <Input
                type="number"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                placeholder="0.00"
                className="text-xl h-12"
                data-testid="opening-cash-input"
              />
            </div>

            {/* Add Register option */}
            <button
              type="button"
              onClick={async () => {
                const name = `Register ${terminals.length + 1}`;
                try {
                  const res = await axios.post(`${API_URL}/api/pos/terminals`, { name, location: '' });
                  setTerminals(prev => [...prev, res.data]);
                  setSelectedTerminal(res.data.id);
                  toast.success(`Added ${name}`);
                } catch (err) {
                  toast.error('Failed to add register');
                }
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              data-testid="add-register-btn"
            >
              <Plus className="h-3 w-3" /> Add another register
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleOpenShift} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-open-shift-btn">
              Open Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <AlertDialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              {canVoidTransaction 
                ? 'This will clear all items from the cart. This action cannot be undone.'
                : 'You do not have permission to void transactions. Please contact a manager.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {canVoidTransaction && (
              <AlertDialogAction onClick={handleVoidTransaction} className="bg-red-600 hover:bg-red-700">
                Void Transaction
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Age Verification Dialog - Enhanced with Camera Scan + USB Scanner */}
      <AgeVerificationDialog
        open={showAgeVerifyDialog}
        onClose={handleAgeVerifyClose}
        onVerified={handleAgeVerifySuccess}
        onDenied={handleAgeVerifyDenied}
        productName={pendingAgeProduct?.name}
      />

      {/* Carton/Pack Splitting Dialog */}
      <CartonPackDialog
        open={showCartonDialog}
        onClose={() => { setShowCartonDialog(false); setPendingCartonProduct(null); }}
        product={pendingCartonProduct}
        onAddToCart={(item) => addItemToCartDirect(item)}
      />

      {/* Quick Add Product Dialog */}
      <Dialog open={showQuickAddDialog} onOpenChange={setShowQuickAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Plus className="h-6 w-6 text-blue-600" /> Quick Add Product
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Add a new product directly from POS. It will be saved to inventory and ready for sale immediately.
              </p>
            </div>
            
            {/* Product Image Section */}
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
              <Label className="text-base font-semibold mb-3 block">Product Image</Label>
              
              <div className="flex gap-4">
                {/* Image Preview */}
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden bg-white dark:bg-slate-800">
                  {quickAddProduct.image_url && !quickAddProduct.use_icon ? (
                    <img src={quickAddProduct.image_url} alt="Product" className="w-full h-full object-cover" />
                  ) : quickAddProduct.use_icon ? (
                    <span className={`${ICON_SIZES[quickAddProduct.icon_size]}`} style={{ color: quickAddProduct.icon_color }}>
                      {quickAddProduct.icon}
                    </span>
                  ) : (
                    <Package className="h-12 w-12 text-slate-300" />
                  )}
                </div>
                
                {/* Image Options */}
                <div className="flex-1 space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateProductImage}
                      disabled={generatingImage || !quickAddProduct.name}
                      className="gap-2"
                    >
                      {generatingImage ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="h-4 w-4" /> Generate with AI</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant={quickAddProduct.use_icon ? "default" : "outline"}
                      size="sm"
                      onClick={() => setQuickAddProduct(prev => ({ ...prev, use_icon: !prev.use_icon }))}
                      className="gap-2"
                    >
                      <Package className="h-4 w-4" /> Use Icon
                    </Button>
                  </div>
                  
                  {/* Icon Options (shown when use_icon is true) */}
                  {quickAddProduct.use_icon && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {(PRODUCT_ICONS[selectedIndustry] || PRODUCT_ICONS['retail_store']).map(icon => (
                          <button
                            key={icon}
                            type="button"
                            onClick={() => setQuickAddProduct(prev => ({ ...prev, icon }))}
                            className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl transition-all ${
                              quickAddProduct.icon === icon 
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' 
                                : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                            }`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 items-center">
                        <Label className="text-xs">Size:</Label>
                        {['small', 'medium', 'large'].map(size => (
                          <Button
                            key={size}
                            type="button"
                            variant={quickAddProduct.icon_size === size ? "default" : "outline"}
                            size="sm"
                            onClick={() => setQuickAddProduct(prev => ({ ...prev, icon_size: size }))}
                            className="h-7 px-2 text-xs capitalize"
                          >
                            {size}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Image URL Input */}
                  {!quickAddProduct.use_icon && (
                    <div>
                      <Label className="text-xs">Or paste image URL</Label>
                      <Input
                        placeholder="https://..."
                        value={quickAddProduct.image_url}
                        onChange={(e) => setQuickAddProduct(prev => ({ ...prev, image_url: e.target.value }))}
                        className="h-9 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Product Name *</Label>
                <Input
                  placeholder="e.g., Marlboro Red 100s"
                  value={quickAddProduct.name}
                  onChange={(e) => setQuickAddProduct(prev => ({ ...prev, name: e.target.value }))}
                  className="h-12"
                />
              </div>
              
              <div>
                <Label>Sell Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="12.99"
                    value={quickAddProduct.sell_price}
                    onChange={(e) => setQuickAddProduct(prev => ({ ...prev, sell_price: e.target.value }))}
                    className="h-12 pl-8"
                  />
                </div>
              </div>
              
              <div>
                <Label>Cost Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="8.00"
                    value={quickAddProduct.cost_price}
                    onChange={(e) => setQuickAddProduct(prev => ({ ...prev, cost_price: e.target.value }))}
                    className="h-12 pl-8"
                  />
                </div>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select 
                  value={quickAddProduct.category}
                  onValueChange={(v) => setQuickAddProduct(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(INDUSTRY_CATEGORIES[selectedIndustry] || INDUSTRY_CATEGORIES['retail_store']).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Barcode / UPC</Label>
                <Input
                  placeholder="Scan or enter"
                  value={quickAddProduct.barcode}
                  onChange={(e) => setQuickAddProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  className="h-12"
                />
              </div>
              
              <div>
                <Label>SKU</Label>
                <Input
                  placeholder="Auto-generated if empty"
                  value={quickAddProduct.sku}
                  onChange={(e) => setQuickAddProduct(prev => ({ ...prev, sku: e.target.value }))}
                  className="h-12"
                />
              </div>
              
              <div>
                <Label>Stock Quantity</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={quickAddProduct.stock_quantity}
                  onChange={(e) => setQuickAddProduct(prev => ({ ...prev, stock_quantity: e.target.value }))}
                  className="h-12"
                />
              </div>
              
              <div>
                <Label>Low Stock Alert</Label>
                <Input
                  type="number"
                  placeholder="5"
                  value={quickAddProduct.low_stock_threshold}
                  onChange={(e) => setQuickAddProduct(prev => ({ ...prev, low_stock_threshold: e.target.value }))}
                  className="h-12"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowQuickAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={loadBulkProductTemplate}
              className="gap-2"
            >
              <Package className="h-4 w-4" /> Bulk Import
            </Button>
            <Button 
              onClick={handleQuickAddProduct}
              disabled={addingProduct || !quickAddProduct.name || !quickAddProduct.sell_price}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {addingProduct ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Add Product</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Package className="h-6 w-6 text-emerald-600" /> Bulk Import Products
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              Import common {businessType?.name || 'retail'} products. Edit prices and details, then import selected items.
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto mt-4">
            <table className="w-full">
              <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                <tr>
                  <th className="p-2 text-left">
                    <input
                      type="checkbox"
                      checked={bulkProducts.every(p => p.selected)}
                      onChange={(e) => setBulkProducts(prev => prev.map(p => ({ ...p, selected: e.target.checked })))}
                      className="rounded"
                    />
                  </th>
                  <th className="p-2 text-left text-sm">Product Name</th>
                  <th className="p-2 text-left text-sm">Category</th>
                  <th className="p-2 text-left text-sm">Cost $</th>
                  <th className="p-2 text-left text-sm">Sell $</th>
                  <th className="p-2 text-left text-sm">Stock</th>
                </tr>
              </thead>
              <tbody>
                {bulkProducts.map((product, idx) => (
                  <tr key={product.id} className={`border-b ${product.selected ? 'bg-emerald-50/50 dark:bg-emerald-950/50' : 'opacity-50'}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={product.selected}
                        onChange={(e) => setBulkProducts(prev => prev.map((p, i) => i === idx ? { ...p, selected: e.target.checked } : p))}
                        className="rounded"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        value={product.name}
                        onChange={(e) => setBulkProducts(prev => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <span className="text-sm text-muted-foreground">{product.category}</span>
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={product.cost_price}
                        onChange={(e) => setBulkProducts(prev => prev.map((p, i) => i === idx ? { ...p, cost_price: e.target.value } : p))}
                        className="h-8 w-20 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={product.sell_price}
                        onChange={(e) => setBulkProducts(prev => prev.map((p, i) => i === idx ? { ...p, sell_price: e.target.value } : p))}
                        className="h-8 w-20 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={product.stock_quantity}
                        onChange={(e) => setBulkProducts(prev => prev.map((p, i) => i === idx ? { ...p, stock_quantity: e.target.value } : p))}
                        className="h-8 w-16 text-sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <DialogFooter className="mt-4 border-t pt-4">
            <div className="flex items-center gap-2 mr-auto text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-emerald-500" />
              {bulkProducts.filter(p => p.selected).length} of {bulkProducts.length} selected
            </div>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkImport}
              disabled={importingBulk || bulkProducts.filter(p => p.selected).length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {importingBulk ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Import {bulkProducts.filter(p => p.selected).length} Products</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Selection Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" /> Select Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, phone..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  loadCustomers(e.target.value);
                }}
                className="h-10"
              />
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCustomerDialog(false);
                  setShowNewCustomerDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> New
              </Button>
            </div>
            
            <div className="max-h-60 overflow-y-auto space-y-2">
              {customers.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No customers found</p>
              ) : (
                customers.map(customer => (
                  <div
                    key={customer.id}
                    className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerDialog(false);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.email || customer.phone}</p>
                      </div>
                      <div className="flex items-center gap-1 text-purple-600">
                        <Star className="h-4 w-4" />
                        <span className="text-sm font-medium">{customer.loyalty_points || 0} pts</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={showNewCustomerDialog} onOpenChange={setShowNewCustomerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-emerald-600" /> New Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="Customer name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                placeholder="(555) 123-4567"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCustomer} disabled={!newCustomer.name}>
              <Plus className="h-4 w-4 mr-1" /> Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promo Code Dialog */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-emerald-600" /> Apply Promo Code
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              className="h-12 text-center text-lg uppercase tracking-wider"
            />
            <p className="text-xs text-muted-foreground text-center">
              Enter a valid promotional code to apply a discount
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyPromo} disabled={!promoCode}>
              <Check className="h-4 w-4 mr-1" /> Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Digital Receipt Dialog */}
      <Dialog open={showDigitalReceiptDialog} onOpenChange={setShowDigitalReceiptDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" /> Send Digital Receipt
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={digitalReceiptData.method === 'email' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setDigitalReceiptData(prev => ({ ...prev, method: 'email' }))}
              >
                <Mail className="h-4 w-4 mr-2" /> Email
              </Button>
              <Button
                variant={digitalReceiptData.method === 'sms' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setDigitalReceiptData(prev => ({ ...prev, method: 'sms' }))}
              >
                <Phone className="h-4 w-4 mr-2" /> SMS
              </Button>
            </div>
            
            {digitalReceiptData.method === 'email' ? (
              <Input
                type="email"
                placeholder="customer@email.com"
                value={digitalReceiptData.email}
                onChange={(e) => setDigitalReceiptData(prev => ({ ...prev, email: e.target.value }))}
              />
            ) : (
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={digitalReceiptData.phone}
                onChange={(e) => setDigitalReceiptData(prev => ({ ...prev, phone: e.target.value }))}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDigitalReceiptDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendDigitalReceipt} disabled={sendingReceipt}>
              {sendingReceipt ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> Send Receipt</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Report Dialog */}
      <Dialog open={showShiftReportDialog} onOpenChange={setShowShiftReportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" /> Shift Report
            </DialogTitle>
          </DialogHeader>
          
          {shiftReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800">
                  <CardContent className="p-4">
                    <p className="text-sm text-emerald-600">Total Sales</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      ${shiftReport.total_sales?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600">Transactions</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {shiftReport.transaction_count || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Cash Sales</p>
                    <p className="text-xl font-bold">${shiftReport.cash_sales?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Card Sales</p>
                    <p className="text-xl font-bold">${shiftReport.card_sales?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Cash Drawer</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Opening</p>
                    <p className="font-medium">${shiftReport.opening_cash?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Expected</p>
                    <p className="font-medium">${shiftReport.expected_cash?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Variance</p>
                    <p className={`font-medium ${(shiftReport.variance || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      ${shiftReport.variance?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftReportDialog(false)}>
              Close
            </Button>
            <Button onClick={() => window.print()} className="gap-2">
              <Printer className="h-4 w-4" /> Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fuel Prepay Dialog */}
      <Dialog open={showFuelPrepayDialog} onOpenChange={setShowFuelPrepayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Fuel className="h-6 w-6 text-orange-500" /> Fuel Prepay
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
              <p className="text-sm text-orange-700 dark:text-orange-300">
                Enter prepay details. After payment, authorize the pump on Gilbarco Passport.
              </p>
            </div>
            
            {/* Pump Number */}
            <div>
              <Label className="text-sm font-medium">Pump Number *</Label>
              <div className="grid grid-cols-6 gap-2 mt-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                  <Button
                    key={num}
                    variant={fuelPrepay.pumpNumber === String(num) ? 'default' : 'outline'}
                    className={`h-12 text-lg font-bold ${fuelPrepay.pumpNumber === String(num) ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    onClick={() => setFuelPrepay(prev => ({ ...prev, pumpNumber: String(num) }))}
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Fuel Grade */}
            <div>
              <Label className="text-sm font-medium">Fuel Grade</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {fuelGrades.map(grade => (
                  <Button
                    key={grade.id}
                    variant={fuelPrepay.grade === grade.id ? 'default' : 'outline'}
                    className={`h-16 flex flex-col items-center justify-center ${
                      fuelPrepay.grade === grade.id 
                        ? grade.id === 'regular' ? 'bg-green-500 hover:bg-green-600' 
                          : grade.id === 'plus' ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                          : grade.id === 'premium' ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-slate-800 hover:bg-slate-900'
                        : ''
                    }`}
                    onClick={() => setFuelPrepay(prev => ({ ...prev, grade: grade.id }))}
                  >
                    <span className="font-bold">{grade.name}</span>
                    <span className="text-xs">${grade.price.toFixed(2)}/gal</span>
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Prepay Amount */}
            <div>
              <Label className="text-sm font-medium">Prepay Amount *</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[10, 20, 30, 40, 50, 60, 80, 100].map(amount => (
                  <Button
                    key={amount}
                    variant={fuelPrepay.amount === String(amount) ? 'default' : 'outline'}
                    className={`h-12 font-bold ${fuelPrepay.amount === String(amount) ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                    onClick={() => setFuelPrepay(prev => ({ ...prev, amount: String(amount) }))}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">Custom:</span>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={fuelPrepay.amount}
                  onChange={(e) => setFuelPrepay(prev => ({ ...prev, amount: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
            
            {/* Customer Name (optional) */}
            <div>
              <Label className="text-sm font-medium">Customer Name (optional)</Label>
              <Input
                placeholder="Walk-in customer"
                value={fuelPrepay.customerName}
                onChange={(e) => setFuelPrepay(prev => ({ ...prev, customerName: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            {/* Summary */}
            {fuelPrepay.pumpNumber && fuelPrepay.amount && (
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Pump #{fuelPrepay.pumpNumber}</span>
                  <span className="text-sm text-muted-foreground">
                    {fuelGrades.find(g => g.id === fuelPrepay.grade)?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-2xl font-bold text-emerald-600">
                    ${parseFloat(fuelPrepay.amount || 0).toFixed(2)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ~{(parseFloat(fuelPrepay.amount || 0) / (fuelGrades.find(g => g.id === fuelPrepay.grade)?.price || 3.29)).toFixed(1)} gallons
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowFuelPrepayDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleFuelPrepay}
              disabled={!fuelPrepay.pumpNumber || !fuelPrepay.amount}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Fuel className="h-4 w-4 mr-2" /> Add Prepay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fuel Change Due Dialog */}
      <Dialog open={showFuelChangeDialog} onOpenChange={setShowFuelChangeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-500" /> Fuel Change Due
            </DialogTitle>
          </DialogHeader>
          
          {selectedPrepay && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span>Pump #{selectedPrepay.pumpNumber}</span>
                  <span>{selectedPrepay.grade?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prepay Amount:</span>
                  <span className="font-bold">${selectedPrepay.amount.toFixed(2)}</span>
                </div>
              </div>
              
              <div>
                <Label>Actual Fuel Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter actual amount"
                  value={actualFuelAmount}
                  onChange={(e) => setActualFuelAmount(e.target.value)}
                  className="mt-1 text-lg"
                />
              </div>
              
              {actualFuelAmount && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Change Due:</span>
                    <span className="text-2xl font-bold text-emerald-600">
                      ${(selectedPrepay.amount - parseFloat(actualFuelAmount || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowFuelChangeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleFuelChange} disabled={!actualFuelAmount}>
              Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active Prepays Panel (shown when gas_station industry) */}
      {selectedIndustry === 'gas_station' && activePrepays.filter(p => p.status === 'active').length > 0 && (
        <div className="fixed bottom-4 left-4 bg-white dark:bg-slate-800 shadow-xl rounded-lg border p-4 w-80 z-40">
          <h4 className="font-semibold text-orange-600 flex items-center gap-2 mb-3">
            <Fuel className="h-4 w-4" /> Active Prepays ({activePrepays.filter(p => p.status === 'active').length})
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {activePrepays.filter(p => p.status === 'active').map(prepay => (
              <div key={prepay.id} className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
                <div>
                  <span className="font-bold">Pump #{prepay.pumpNumber}</span>
                  <span className="text-sm text-muted-foreground ml-2">${prepay.amount.toFixed(2)}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedPrepay(prepay);
                      setShowFuelChangeDialog(true);
                    }}
                    className="h-7 px-2 text-emerald-600 hover:text-emerald-700"
                  >
                    Complete
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => cancelPrepay(prepay.id)}
                    className="h-7 px-2 text-red-600 hover:text-red-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Held Orders Dialog */}
      <Dialog open={showHeldOrdersDialog} onOpenChange={setShowHeldOrdersDialog}>
        <DialogContent className="max-w-md" data-testid="held-orders-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" /> Held Orders ({heldOrders.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {heldOrders.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">No held orders</p>
            ) : (
              heldOrders.map(order => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm">{order.customer}</p>
                    <p className="text-xs text-muted-foreground">{order.items.length} items — ${order.total} — {order.heldAt}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                      onClick={() => recallOrder(order.id)} data-testid={`recall-${order.id}`}>
                      Recall
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500"
                      onClick={() => setHeldOrders(prev => prev.filter(o => o.id !== order.id))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund/Return Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-red-500" /> Process Return/Refund
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Enter transaction ID or receipt #"
                value={refundSearch} onChange={(e) => setRefundSearch(e.target.value)}
                className="h-10" data-testid="refund-search" />
              <Button onClick={handleRefundLookup} className="h-10 shrink-0">Lookup</Button>
            </div>

            {refundTransaction && (
              <>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border text-sm">
                  <p className="font-medium">Transaction #{refundTransaction.transaction_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {refundTransaction.cashier_name} — ${refundTransaction.total?.toFixed(2)} — {refundTransaction.payment_method}
                  </p>
                </div>

                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {refundItems.map((item, idx) => (
                    <label key={idx} className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm cursor-pointer">
                      <input type="checkbox" checked={item.refund}
                        onChange={() => setRefundItems(prev => prev.map((it, i) => i === idx ? {...it, refund: !it.refund} : it))}
                        className="rounded" />
                      <span className="flex-1">{item.name} x{item.quantity}</span>
                      <span className="font-medium">${item.line_total?.toFixed(2)}</span>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="text-sm font-medium">Reason for Return *</label>
                  <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="e.g. Customer changed mind, defective product..."
                    data-testid="refund-reason" />
                </div>

                <Button onClick={processRefund} className="w-full h-10 bg-red-600 hover:bg-red-700"
                  disabled={!refundReason.trim()} data-testid="process-refund-btn">
                  Process Refund
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSTerminal;
